/**
 * The earlier links a final payment request replaces (ADR 0139).
 *
 * The final request bills a payment nobody has started in full —
 * `paymentInitiated` below, Jonathon's rule of 2026-09-13. Until 2026-09-19
 * the link that first asked for that payment stayed live beside it, so a
 * customer holding both could pay the deposit twice: once on the old link and
 * once inside the balance. Jonathon, 2026-09-19: "raising the final request
 * should cancel the unpaid earlier link. Make sure somebody is told if Mercury
 * refuses the cancel."
 *
 * So the set of links to cancel is exactly the set the final request bills
 * again: an earlier slot, still being asked for, with an invoice at Mercury,
 * and not started. A link whose payment has started is never touched — its
 * money is on its way, and the final request already left it off the bill.
 *
 * The mercury Edge Function acts on a copy of this (Deno cannot import the
 * workspace), held to it by supabase/test/replaced-links-match.test.ts.
 */

import { paymentInitiated } from "./moneyInTheBank.js";
import { FINAL_SLOT } from "./paymentSchedule.js";

/** A `mercury_payment_requests` row, as much of it as this rule reads. */
export interface LinkOnTheJob {
  slot: number | null;
  state: string;
  mercury_invoice_id: string | null;
  payment_started_at: string | null;
}

/**
 * The links raising the final request cancels.
 *
 * `pending` is included as well as `sent`: a voided proposal puts its deposit
 * row back to pending with the invoice still attached when Mercury refuses the
 * withdraw (ADR 0123), and that link still takes money.
 */
export function linksTheFinalReplaces<T extends LinkOnTheJob>(
  rows: readonly T[],
): T[] {
  return rows.filter(
    (row) =>
      Number(row.slot) !== FINAL_SLOT &&
      (row.state === "pending" || row.state === "sent") &&
      Boolean(row.mercury_invoice_id) &&
      !paymentInitiated({
        state: row.state,
        amountCents: null,
        paymentStartedAt: row.payment_started_at,
      }),
  );
}

/**
 * Links still live on a job whose final request has been raised — what the
 * balance screen warns about.
 *
 * Read off the rows rather than off whether the cancel ran, so a cancel that
 * never happened at all (the app lost its connection, the function was down)
 * shows exactly as a refused one does.
 */
export function linksStillLiveBesideTheFinal<T extends LinkOnTheJob>(
  rows: readonly T[],
): T[] {
  const finalRaised = rows.some(
    (row) =>
      Number(row.slot) === FINAL_SLOT &&
      row.state !== "cancelled" &&
      row.state !== "failed",
  );
  return finalRaised ? linksTheFinalReplaces(rows) : [];
}

/** What `cancelled_reason` says on a link the final request replaced. */
export const REPLACED_BY_FINAL_REASON =
  "Replaced by the final payment request, which bills this payment in full.";
